/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useEffect } from 'react';
import { Button, Checkbox, Grid, Icon, Input, Tree } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../locales/en-US/descriptors';
import VirtualMetersModal from './VirtualMetersModal';
import VirtualMeterForm from './VirtualMeterForm';
import '../../scss/VirtualMeters.scss';
import { MAGIC_VALUE } from '../shared/Utils/Constants';
import {
  iconForElectricMeter,
  isUndefinedOrNullCheck
} from './ElectircMeterIcon';
import { IAny } from '../DashboardKPIWidgets/IEnergyManagementProps';
import { withLocalization } from '../../locales/withLocalization';

interface ICreateVirtualMeterProps {
  siteID?: string;
  siteName: string;
  selectedMeters: string[];
  loadMeters(): Promise<ISiteMeter[]>;
  save(payload: ISaveRequest): void;
  cancel(): void;
}

interface IMeters {
  MeterId: string;
  MeterName: string;
  Selected: boolean;
  MeterType?: string;
  containsElementIds?: string;
  suppliesSpatialElement?: IAny;
}

interface ISiteMeter {
  id: string;
  name: string;
  types?: string[];
  containsElementIds?: string[];
  suppliesSpatialElement?: IAny;
}

interface ISaveRequest {
  virtualMeterName: string;
  description: string;
  elements: string[];
}

const CreateVirtualMeter = (props: ICreateVirtualMeterProps) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [siteMeters, setSiteMeters] = React.useState<IMeters[]>([]);
  const [virtualMeterName, setVirtualMeterName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [metersModal, setMetersModal] = React.useState(false);

  const d = descriptors.virtualMeters;
  const [t] = useTranslation();

  const { siteName } = props;
  const Item = Tree.Content.Item;
  useEffect(() => {
    loadMeters();
  }, []);

  const loadMeters = async () => {
    const sites: ISiteMeter[] = await props.loadMeters();
    const _siteMeters: IMeters[] = sites.map((item) => ({
      MeterId: item.id,
      MeterName: item.name,
      containsElementIds: isUndefinedOrNullCheck(item)
        ? `(${item.containsElementIds?.length})`
        : '',
      MeterType: item.types?.find(
        (val) => val === 'VirtualElectricMeter' || val === 'ElectricMeter'
      ),
      Selected:
        (props.selectedMeters &&
          props.selectedMeters.length > 0 &&
          props.selectedMeters.includes(item.id)) ||
        false,
      suppliesSpatialElement: item.suppliesSpatialElement
    }));
    setSiteMeters(_siteMeters);
  };

  const handleMeterSelection = (meter: IMeters) => {
    const index = siteMeters.indexOf(meter);
    const siteMetersCopy = [...siteMeters];
    siteMetersCopy[index].Selected = !siteMetersCopy[index].Selected;
    setSiteMeters(siteMetersCopy);
  };

  const save = () => {
    const payload = {
      virtualMeterName: virtualMeterName,
      description: description,
      elements: siteMeters
        .filter((item) => item.Selected)
        .map((item) => item.MeterId)
    };
    props.save(payload);
  };

  const renderMetersUnderSiteTree = (
    site: IMeters,
    siteKey: number,
    siteID: IAny
  ) => {
    if (site.MeterName.toLowerCase().includes(searchTerm.toLowerCase()))
      return (
        <Item
          key={siteKey}
          className="custom-tree-item"
          title={
            <>
              <Checkbox
                checked={site.Selected}
                onChange={() => handleMeterSelection(site)}
              />
              {iconForElectricMeter(
                site.MeterType,
                site.suppliesSpatialElement,
                siteID
              )}
              <span>{site.MeterName}</span>
              <span className="VMCount">{site.containsElementIds}</span>
            </>
          }
        />
      );
    else return <></>;
  };

  const invalidMetersSelection =
    siteMeters.filter((r) => r.Selected).length <= MAGIC_VALUE.ONE;

  return (
    <div className="emaas-virtual-meter">
      <div className="emaas-virtual-meter-section">
        <Grid>
          <Grid.Row>
            <Grid.Column width={4} className="meters-section-column">
              <div className="meters-section">
                <span>
                  {t(d.SELECTED, {
                    count: siteMeters.filter((r) => r.Selected).length
                  })}
                </span>
                <Tree>
                  <Tree.Header>
                    <div className="search-textbox">
                      <Input
                        fluid
                        icon={<Icon root="common" name="search" size="small" />}
                        iconPosition="left"
                        placeholder={t(d.SEARCHMETERS)}
                        value={searchTerm}
                        onChange={(text) => setSearchTerm(text)}
                      />
                    </div>
                  </Tree.Header>
                  <Tree.Content maxHeight={580}>
                    <Item
                      expanded
                      title={
                        <>
                          <Checkbox
                            disabled
                            indeterminate={
                              siteMeters.filter((r) => r.Selected).length > 0 &&
                              siteMeters.filter((r) => !r.Selected).length > 0
                            }
                            checked={
                              siteMeters.filter((r) => !r.Selected).length === 0
                            }
                          />
                          <span>{siteName}</span>
                        </>
                      }
                    >
                      {siteMeters &&
                        siteMeters.map((site, siteKey) => {
                          return renderMetersUnderSiteTree(
                            site,
                            siteKey,
                            props.siteID
                          );
                        })}
                    </Item>
                  </Tree.Content>
                </Tree>
              </div>
            </Grid.Column>
            <VirtualMeterForm
              name={virtualMeterName}
              description={description}
              invalidMetersSelection={invalidMetersSelection}
              setName={setVirtualMeterName}
              setDescription={setDescription}
            />
          </Grid.Row>
        </Grid>
      </div>
      <div className="buttons-section">
        <Button
          type="primary"
          content={t(d.SAVE)}
          disabled={invalidMetersSelection || !virtualMeterName || !description}
          onClick={save}
        />
        <Button type="secondary" content={t(d.CANCEL)} onClick={props.cancel} />
      </div>
      <VirtualMetersModal
        modalOpen={metersModal}
        hideModal={() => setMetersModal(false)}
      />
    </div>
  );
};

export default withLocalization<ICreateVirtualMeterProps>(CreateVirtualMeter);
